/**
 * Orbe Liquid Glass — tier 2 edge refraction (feDisplacementMap).
 * Spec: .cursor/rules/liquid-glass.mdc
 * Applies filter to [data-orbe-glass-surface] only — never text/content.
 */
(function () {
  'use strict';

  var FILTER_NS = 'http://www.w3.org/2000/svg';
  var XLINK_NS = 'http://www.w3.org/1999/xlink';
  var DEFS_ID = 'orbe-glass-svg-defs';
  var SCALE = { idle: 4, hover: 10, press: 13 };
  var EDGE_BAND = 14;
  var RESIZE_DEBOUNCE_MS = 150;
  var filterCounter = 0;
  var instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function ensureDefsSvg() {
    var existing = document.getElementById(DEFS_ID);
    if (existing) return existing.querySelector('defs');

    var svg = document.createElementNS(FILTER_NS, 'svg');
    svg.setAttribute('id', DEFS_ID);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute(
      'style',
      'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none'
    );
    var defs = document.createElementNS(FILTER_NS, 'defs');
    svg.appendChild(defs);
    document.body.appendChild(svg);
    return defs;
  }

  function signedDistanceRoundedRect(x, y, w, h, r) {
    var cx = Math.max(r, Math.min(x, w - r));
    var cy = Math.max(r, Math.min(y, h - r));
    var dx = x - cx;
    var dy = y - cy;
    var distCorner = Math.sqrt(dx * dx + dy * dy) - r;
    var distEdgeX = Math.min(x, w - x);
    var distEdgeY = Math.min(y, h - y);
    if (x >= r && x <= w - r) return distEdgeY;
    if (y >= r && y <= h - r) return distEdgeX;
    return -distCorner;
  }

  function buildDisplacementMap(width, height, radius, dispScale) {
    var w = Math.max(1, Math.round(width));
    var h = Math.max(1, Math.round(height));
    var r = Math.min(radius, w / 2, h / 2);
    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    var imageData = ctx.createImageData(w, h);
    var data = imageData.data;
    var band = Math.min(EDGE_BAND, Math.max(4, r * 0.45));

    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        var edge = signedDistanceRoundedRect(x + 0.5, y + 0.5, w, h, r);
        var rx = 128;
        var gy = 128;

        if (edge > 0 && edge <= band) {
          var t = 1 - edge / band;
          var strength = t * t * dispScale;
          var nx = (x + 0.5 - w / 2) / (w / 2 || 1);
          var ny = (y + 0.5 - h / 2) / (h / 2 || 1);
          var len = Math.sqrt(nx * nx + ny * ny) || 1;
          rx = Math.max(0, Math.min(255, Math.round(128 - (nx / len) * strength * 12)));
          gy = Math.max(0, Math.min(255, Math.round(128 - (ny / len) * strength * 12)));
        }

        data[i] = rx;
        data[i + 1] = gy;
        data[i + 2] = 128;
        data[i + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }

  function applyFilter(surface, mapDataUrl, scale) {
    var defs = ensureDefsSvg();
    filterCounter += 1;
    var id = 'orbe-glass-filter-' + filterCounter;
    var filter = document.createElementNS(FILTER_NS, 'filter');
    filter.setAttribute('id', id);
    filter.setAttribute('x', '-15%');
    filter.setAttribute('y', '-15%');
    filter.setAttribute('width', '130%');
    filter.setAttribute('height', '130%');
    filter.setAttribute('color-interpolation-filters', 'sRGB');

    var feImage = document.createElementNS(FILTER_NS, 'feImage');
    feImage.setAttribute('result', 'dispMap');
    feImage.setAttribute('href', mapDataUrl);
    feImage.setAttributeNS(XLINK_NS, 'xlink:href', mapDataUrl);

    var feDisp = document.createElementNS(FILTER_NS, 'feDisplacementMap');
    feDisp.setAttribute('in', 'SourceGraphic');
    feDisp.setAttribute('in2', 'dispMap');
    feDisp.setAttribute('scale', String(scale));
    feDisp.setAttribute('xChannelSelector', 'R');
    feDisp.setAttribute('yChannelSelector', 'G');

    filter.appendChild(feImage);
    filter.appendChild(feDisp);
    defs.appendChild(filter);

    // Cap defs growth
    while (defs.childNodes.length > 24) {
      defs.removeChild(defs.firstChild);
    }

    surface.style.filter = 'url(#' + id + ')';
  }

  function OrbeGlassInstance(root) {
    this.root = root;
    this.surface = root.querySelector('[data-orbe-glass-surface]');
    this.state = 'idle';
    this.resizeTimer = null;
    this.boundEnter = this.onEnter.bind(this);
    this.boundLeave = this.onLeave.bind(this);
    this.boundDown = this.onDown.bind(this);
    this.boundUp = this.onUp.bind(this);
    this.boundResize = this.onResize.bind(this);

    if (!this.surface) {
      root.setAttribute('data-orbe-glass-tier', '0');
      return;
    }

    if (prefersReducedMotion()) {
      root.setAttribute('data-orbe-glass-tier', '0');
      return;
    }

    root.setAttribute('data-orbe-glass-tier', '2');
    this.update('idle');
    root.addEventListener('pointerenter', this.boundEnter);
    root.addEventListener('pointerleave', this.boundLeave);
    root.addEventListener('pointerdown', this.boundDown);
    root.addEventListener('pointerup', this.boundUp);
    window.addEventListener('resize', this.boundResize);
  }

  OrbeGlassInstance.prototype.update = function (state) {
    if (!this.surface || this.root.getAttribute('data-orbe-glass-tier') !== '2') return;
    this.state = state || this.state;
    var scale = SCALE[this.state] || SCALE.idle;
    var w = this.root.offsetWidth;
    var h = this.root.offsetHeight;
    if (w < 4 || h < 4) return;
    // Pill by default; cards pass their real corner radius (px) via data-orbe-glass-radius
    var radiusAttr = parseFloat(this.root.getAttribute('data-orbe-glass-radius'));
    var radius = isNaN(radiusAttr) ? h / 2 : radiusAttr;
    var map = buildDisplacementMap(w, h, radius, scale);
    applyFilter(this.surface, map, scale);
  };

  OrbeGlassInstance.prototype.onEnter = function () {
    this.update('hover');
  };

  OrbeGlassInstance.prototype.onLeave = function () {
    this.update('idle');
  };

  OrbeGlassInstance.prototype.onDown = function () {
    this.update('press');
  };

  OrbeGlassInstance.prototype.onUp = function () {
    this.update('hover');
  };

  OrbeGlassInstance.prototype.onResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () {
      self.update('idle');
    }, RESIZE_DEBOUNCE_MS);
  };

  OrbeGlassInstance.prototype.destroy = function () {
    this.root.removeEventListener('pointerenter', this.boundEnter);
    this.root.removeEventListener('pointerleave', this.boundLeave);
    this.root.removeEventListener('pointerdown', this.boundDown);
    this.root.removeEventListener('pointerup', this.boundUp);
    window.removeEventListener('resize', this.boundResize);
    clearTimeout(this.resizeTimer);
    if (this.surface) this.surface.style.filter = '';
    instances.delete(this.root);
  };

  function initGlass(root) {
    if (!root) return null;
    if (instances.has(root)) {
      var existing = instances.get(root);
      if (existing && existing.update) existing.update('idle');
      return existing;
    }
    var instance = new OrbeGlassInstance(root);
    instances.set(root, instance);
    return instance;
  }

  function destroyGlass(root) {
    if (!root) return;
    var instance = instances.get(root);
    if (instance && instance.destroy) instance.destroy();
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-orbe-glass]').forEach(initGlass);
  }

  function destroyAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    // Destroy tracked instances under scope, including recently deactivated pills
    root.querySelectorAll('.orbe-glass, [data-orbe-glass]').forEach(function (el) {
      destroyGlass(el);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-orbe-glass]')) {
      initAll(event.target);
    }
  });

  window.OrbeGlass = {
    init: initGlass,
    destroy: destroyGlass,
    initAll: initAll,
    destroyAll: destroyAll,
  };
})();
